'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useProjectsData } from '@/hooks/useProjectsData';
import type { SourceRunStatus } from '@/lib/sources/source-types';
import { countByStatus, formatExactDate } from '@/lib/sources/source-utils';
import { DC_REFRESH_EVENT } from '@/lib/dashboard/header-config';
import { useHeaderSearch } from '@/hooks/useHeaderSearch';
import { KpiCard } from '@/components/ui/kpi-card';
import { ProjectsErrorState } from '@/components/projects/states/ProjectsErrorState';
import { SourcesPageSkeleton } from '@/components/sources/states/SourcesPageSkeleton';
import { SourcesEmptyState } from '@/components/sources/states/SourcesEmptyState';
import { SourcesTable } from '@/components/sources/SourcesTable';
import { SourceDetailSheet } from '@/components/sources/SourceDetailSheet';

export default function SourcesPage() {
  const { sourceStatuses, lastUpdatedAt, isLoading, error, refetch } =
    useProjectsData();
  const { searchQuery } = useHeaderSearch({
    placeholder: 'Search sources...',
    ariaLabel: 'Search sources',
  });

  // Listen for refresh event from layout header
  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DC_REFRESH_EVENT, handler);
    return () => window.removeEventListener(DC_REFRESH_EVENT, handler);
  }, [refetch]);

  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());
  const [localStatuses, setLocalStatuses] = useState<Map<string, SourceRunStatus>>(new Map());
  const [selectedSource, setSelectedSource] = useState<SourceRunStatus | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Merge local overrides with hook data
  const mergedStatuses = useMemo(() => {
    return sourceStatuses.map((s) => localStatuses.get(s.sourceId) ?? s);
  }, [sourceStatuses, localStatuses]);

  const mergedStatusesRef = useRef(mergedStatuses);
  useEffect(() => {
    mergedStatusesRef.current = mergedStatuses;
  }, [mergedStatuses]);

  const counts = useMemo(() => countByStatus(mergedStatuses), [mergedStatuses]);

  const orderedIds = useMemo(
    () => mergedStatuses.map((s) => s.sourceId),
    [mergedStatuses]
  );
  const currentIndex = selectedSource
    ? orderedIds.indexOf(selectedSource.sourceId)
    : -1;

  const handleRunSource = useCallback(
    (sourceId: string, { silent = false } = {}) => {
      setRunningIds((prev) => {
        if (prev.has(sourceId)) return prev;
        return new Set(prev).add(sourceId);
      });
      if (!silent) toast.info('Explorer started…');

      setTimeout(() => {
        setRunningIds((prev) => {
          const next = new Set(prev);
          next.delete(sourceId);
          return next;
        });

        // Update lastRunAt in local state (use ref for latest data)
        const original = mergedStatusesRef.current.find((s) => s.sourceId === sourceId);
        if (original) {
          const updated = { ...original, lastRunAt: new Date().toISOString() };
          setLocalStatuses((prev) => new Map(prev).set(sourceId, updated));
          // Also update the selected source if it's the same
          setSelectedSource((prev) =>
            prev?.sourceId === sourceId ? updated : prev
          );
        }

        if (!silent) toast.success('Explorer completed');
      }, 1500);
    },
    []
  );

  const handleOpenSource = useCallback((source: SourceRunStatus) => {
    setSelectedSource(source);
    setSheetOpen(true);
  }, []);

  const handleNavigate = useCallback(
    (nextIndex: number) => {
      const nextSource = mergedStatuses[nextIndex];
      if (nextSource) {
        setSelectedSource(nextSource);
        setSheetOpen(true);
      }
    },
    [mergedStatuses]
  );

  // Loading
  if (isLoading) {
    return <SourcesPageSkeleton />;
  }

  // Hard error
  if (error) {
    return (
      <ProjectsErrorState
        error={error}
        onRetry={refetch}
        sourceStatuses={sourceStatuses}
      />
    );
  }

  // Empty
  if (mergedStatuses.length === 0) {
    return (
      <div className="p-4 lg:p-6">
        <SourcesEmptyState />
      </div>
    );
  }

  const lastRunDisplay = lastUpdatedAt
    ? formatExactDate(lastUpdatedAt)
    : 'Never';

  return (
    <div className="p-4 lg:p-6">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total Sources" value={counts.total} />
        <KpiCard label="OK" value={counts.ok} variant="ok" />
        <KpiCard label="Failed" value={counts.error} variant="error" />
        <KpiCard label="Last Explorerrun" value={lastRunDisplay} />
      </div>

      {/* Sources table */}
      <SourcesTable
        sources={mergedStatuses}
        onRunSource={handleRunSource}
        onOpen={handleOpenSource}
        runningSourceIds={runningIds}
        searchQuery={searchQuery}
      />

      {/* Detail sheet */}
      <SourceDetailSheet
        source={selectedSource}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onRunSource={handleRunSource}
        isRunning={selectedSource ? runningIds.has(selectedSource.sourceId) : false}
        context={{ orderedIds, currentIndex, onNavigate: handleNavigate }}
      />
    </div>
  );
}

